import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Clock, FileText, MessageSquarePlus, Search } from 'lucide-react';
import { useWorkframeTasks } from '../../hooks/useWorkframes';

// Columna 3 del Cuadro de Trabajo: Bitácora (integración main→dev, epic D1.3). Historial
// cronológico lineal de las tareas del documento seleccionado + su subárbol recursivo. Los
// datos vienen de `useWorkframeTasks` (React Query, key bajo `['tasks','list',…]` para que el
// socket `task_*` ya la invalide). La tarea activa se superpone en memoria para reflejar
// ediciones del panel derecho antes del round-trip del socket.

function extractSnippet(htmlOrText, maxChars = 90) {
    if (!htmlOrText) return '';
    const text = String(htmlOrText).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length <= maxChars ? text : `${text.substring(0, maxChars)}...`;
}

function extractCommentSnippet(content, fallback) {
    let text = typeof content === 'string' ? content : (content?.content || '');
    text = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!text) return fallback;
    return text.length <= 15 ? text : `${text.substring(0, 15)}...`;
}

function formatDateTime(isoString, locale) {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function WorkframeBitacoraColumn({
    workframeId,
    selectedDocId,
    activeTaskId,
    activeTask,
    onSelectTask,
    onCreateTask,
    onAddSuggestion,
    onTasksLoaded,
}) {
    const { t, i18n } = useTranslation('tasks');
    const { data: rawTasks = [], isLoading } = useWorkframeTasks(workframeId, selectedDocId);
    const [searchTerm, setSearchTerm] = React.useState('');

    const tasks = useMemo(() => {
        let filtered = rawTasks;
        if (searchTerm.trim()) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(task => 
                (task.title || '').toLowerCase().includes(lowerSearch) ||
                (task.description || '').toLowerCase().includes(lowerSearch) ||
                (task.creator_nick || '').toLowerCase().includes(lowerSearch) ||
                (task.assignee || '').toLowerCase().includes(lowerSearch) ||
                (task.doc_title || '').toLowerCase().includes(lowerSearch)
            );
        }

        if (!activeTask?.id) return filtered;
        return filtered.map((task) => (task.id === activeTask.id
            ? {
                ...task,
                ...activeTask,
                creator_avatar: task.creator_avatar || activeTask.creator_avatar,
                creator_nick: task.creator_nick || activeTask.creator_nick,
                doc_title: task.doc_title || activeTask.doc_title,
                structured_comments: activeTask.structured_comments !== undefined
                    ? activeTask.structured_comments
                    : task.structured_comments,
            }
            : task));
    }, [rawTasks, activeTask, searchTerm]);

    useEffect(() => {
        if (!isLoading) onTasksLoaded?.(tasks);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawTasks, isLoading]);

    return (
        <div className="wf-column wf-column-bitacora">
            <div className="wf-bitacora-header" style={{ padding: 0 }}>
                <div className="wf-search-box" style={{ borderBottom: 'none' }}>
                    <Search size={14} className="wf-search-icon" />
                    <input
                        type="text"
                        placeholder={t('workframe.bitacora.searchPlaceholder', { defaultValue: 'Buscar en bitácora...' })}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="wf-search-input"
                    />
                </div>
            </div>

            <div className="wf-timeline-scroll">
                {isLoading && tasks.length === 0 ? (
                    <div className="wf-empty-state">{t('workframe.bitacora.loading')}</div>
                ) : tasks.length === 0 ? (
                    <div className="wf-empty-state">
                        <span>{t('workframe.bitacora.empty')}</span>
                        <button className="wf-create-first-btn" onClick={() => onCreateTask?.(selectedDocId)}>
                            <Plus size={13} /> {t('workframe.bitacora.addFirst')}
                        </button>
                    </div>
                ) : (
                    <div className="wf-timeline-container">
                        {tasks.map((task, idx) => {
                            const isSelected = activeTaskId === task.id;
                            const isLast = idx === tasks.length - 1;
                            const snippet = extractSnippet(task.description);
                            const tagList = task.tags ? task.tags.split(',').map((s) => s.trim()).filter(Boolean) : [];
                            const avatar = task.creator_avatar || task.assignee_avatar;
                            const userNick = task.creator_nick || task.assignee || t('workframe.bitacora.user');
                            const suggestions = Array.isArray(task.structured_comments) ? task.structured_comments : [];

                            return (
                                <div key={task.id} className={`wf-timeline-item ${isSelected ? 'active' : ''}`}>
                                    <div className="wf-timeline-axis">
                                        <div className={`wf-timeline-dot ${isSelected ? 'active' : ''}`} />
                                        {!isLast && <div className="wf-timeline-line" />}
                                    </div>

                                    <div className="wf-timeline-content">
                                        <div className="wf-timeline-time">{formatDateTime(task.created_at, i18n.language)}</div>

                                        <div
                                            className={`wf-task-card ${isSelected ? 'selected' : ''}`}
                                            onClick={() => onSelectTask?.(task.id, task)}
                                        >
                                            <div className="wf-card-title">{task.title || t('workframe.bitacora.untitledUpdate')}</div>

                                            {task.doc_title && (
                                                <div
                                                    className={`wf-card-doc-badge ${String(task.doc_id) === String(selectedDocId) ? 'active' : ''}`}
                                                    title={t('workframe.bitacora.docBadge', { title: task.doc_title })}
                                                >
                                                    <FileText size={10} />
                                                    <span>{task.doc_title}</span>
                                                </div>
                                            )}

                                            {snippet && <div className="wf-card-snippet">{snippet}</div>}

                                            {tagList.length > 0 && (
                                                <div className="wf-card-tags">
                                                    {tagList.map((tag) => (
                                                        <span key={tag} className="wf-tag-chip">#{tag}</span>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="wf-card-footer">
                                                <div className="wf-card-duration">
                                                    <Clock size={12} />
                                                    <span>{task.tiempo || t('workframe.bitacora.noTime')}</span>
                                                </div>

                                                <div className="wf-card-footer-right">
                                                    <button
                                                        type="button"
                                                        className="wf-card-suggestion-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (onAddSuggestion) onAddSuggestion(task.id, task);
                                                            else onSelectTask?.(task.id, task);
                                                        }}
                                                        title={t('workframe.bitacora.addSuggestion')}
                                                    >
                                                        <MessageSquarePlus size={13} />
                                                    </button>

                                                    <div className="wf-card-user" title={userNick}>
                                                        {avatar
                                                            ? <img src={avatar} alt={userNick} className="wf-user-avatar" />
                                                            : <div className="wf-user-avatar-fallback">{userNick.substring(0, 2).toUpperCase()}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {suggestions.length > 0 && (
                                            <div className="wf-subcards-list">
                                                {suggestions.map((sc, scIdx) => {
                                                    const scSnippet = extractCommentSnippet(sc.content, t('workframe.bitacora.suggestionFallback'));
                                                    const scNick = sc.author_name || t('workframe.bitacora.user');
                                                    return (
                                                        <div
                                                            key={sc.comment_id || `sc-${scIdx}`}
                                                            className="wf-suggestion-subcard"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onSelectTask?.(task.id, task);
                                                            }}
                                                            title={t('workframe.bitacora.suggestionBy', { name: scNick, snippet: scSnippet })}
                                                        >
                                                            <div className="wf-subcard-text">{scSnippet}</div>
                                                            <div className="wf-subcard-footer">
                                                                {sc.author_avatar
                                                                    ? <img src={sc.author_avatar} alt={scNick} className="wf-subcard-avatar" />
                                                                    : <div className="wf-subcard-avatar-fallback">{scNick.substring(0, 2).toUpperCase()}</div>}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="wf-bitacora-footer" style={{ padding: 'var(--space-3)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <button className="wf-add-update-btn" onClick={() => onCreateTask?.(selectedDocId)} title={t('workframe.bitacora.addUpdate')} style={{ width: '100%', justifyContent: 'center' }}>
                    <Plus size={14} />
                    <span>{t('workframe.bitacora.addUpdate')}</span>
                </button>
            </div>
        </div>
    );
}
